import { useEffect, useState } from "react";
import { db, storage } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject
} from "firebase/storage";
import { useNavigate } from "react-router-dom";

const fixedTags = ["3D", "VFX", "Animation", "Motion Graphics", "Graphics Design"];

export default function Dashboard() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tag, setTag] = useState("3D");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [projects, setProjects] = useState([]);

  const navigate = useNavigate();

  const handleUpload = async () => {
    if (!title || !description || files.length === 0) {
      alert("Please fill out all fields and select files.");
      return;
    }

    setUploading(true);
    const media = [];

    const docRef = await addDoc(collection(db, "projects"), {
      title,
      description,
      tag,
      timestamp: serverTimestamp(),
    });

    const projectId = docRef.id;

    for (const file of files) {
      const fileType = file.type.startsWith("video") ? "video" : "image";
      const fileRef = ref(storage, `projects/${projectId}/${file.name}`);
      const uploadTask = uploadBytesResumable(fileRef, file);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const prog = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setProgress(prog);
          },
          reject,
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            media.push({ type: fileType, url });
            resolve();
          }
        );
      });
    }

    const coverImage = media.find((m) => m.type === "image")?.url || media[0].url;

    await updateDoc(doc(db, "projects", projectId), {
      media,
      coverImage,
    });

    setTitle("");
    setDescription("");
    setTag("3D");
    setFiles([]);
    setUploading(false);
    setProgress(0);
    fetchProjects();
  };

  const fetchProjects = async () => {
    const q = query(collection(db, "projects"), orderBy("timestamp", "desc"));
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setProjects(data);
  };

  const handleDelete = async (project) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    try {
      if (project.media && Array.isArray(project.media)) {
        await Promise.all(
          project.media.map((item) => deleteObject(ref(storage, item.url)))
        );
      }

      await deleteDoc(doc(db, "projects", project.id));

      console.log("Deleted from Firestore and Storage");
      fetchProjects();
    } catch (err) {
      console.error("Error deleting project:", err);
      alert("An error occurred while deleting.");
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return (
    <section className="section_admin">
      {uploading && (
        <div className="progress-container">
          <div
            className="progress-bar"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}

      <form className="upload-form">
        <input
          type="text"
          className="input-field"
          placeholder="Project Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          className="input-field"
          placeholder="Project Description"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <select
          className="dropdown"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        >
          {fixedTags.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <input
          id="fileInput"
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={(e) => setFiles([...e.target.files])}
        />
        {files.length > 0 && (
          <ul className="text-sm list-disc list-inside bg-gray-100 p-3 rounded text-gray-700">
            {files.map((file, idx) => (
              <li key={idx}>{file.name}</li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="upload-button"
          onClick={handleUpload}
          disabled={uploading}
        >
          {uploading ? "Uploading..." : "Upload"}
        </button>
      </form>

      <h2 className="section-title text-gray-700">Uploaded Projects</h2>
      <div className="row_new">
        {projects.map((project) => (
          <div
            key={project.id}
            className="portfolio-item padd-15 filter-item project-card"
          >
            <div className="portfolio-item-inner">
              <div className="portfolio-img">
                {project.coverImage?.endsWith(".mp4") ? (
                  <div className="video-wrapper">
                    <video
                      src={project.coverImage}
                      muted
                      controls
                      preload="auto"
                      playsInline
                    />
                  </div>
                ) : (
                  <div className="image-wrapper">
                    <img
                      src={project.coverImage}
                      alt={project.title}
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
              <div className="p-3 text-left">
                <h3 className="video_title_tag">{project.title}</h3>
                <p className="video_title_tag">{project.tag}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/dashboard/edit/${project.id}`)}
                    className="dashboard_edit_del_btn"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(project)}
                    className="dashboard_edit_del_btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
